import React from 'react'
import {Box, Button, Flex, Switch, Text} from '@chakra-ui/react'

import {ContentControl} from './subcomps/ContentControl'
import {Feature} from '../Feature/Feature'
import {HyperlinkAttachment} from './subcomps/HyperlinkAttachment'
import {MediaAttachment} from './subcomps/MediaAttachment'
import {ActionButton} from '../ActionButton/ActionButton'
import {
  useComposeMicropostState,
  useComposeMicropostActions,
} from './contextManagers/MicropostManager'
import {useSegments} from './contextManagers/SegmentManager'
import {useAttachmentControlsState} from './contextManagers/AttachmentControlsManager'
import {useStatusToast} from '../../app/ToastManager'
import {useReleaseFlags} from '../../hooks/useReleaseFlags'
import {useMicropostsStore} from '../../stores/MicropostsStore'
import {useModalContext} from './subcomps/Modal'
import {INSERT_PLACEHOLDER_ID} from '../../constants'
import copy from './copy.json'
import {CustomIcon} from '../CustomIcon/CustomIcon'
import {appTheme} from '../../app/theme'
import {FeatureName} from '../../types'

const SEGMENT_MICROPOSTS_FEATURE: FeatureName = 'segment-microposts'

const Header: React.FC<React.PropsWithChildren> = () => {
  const {publishDate} = useComposeMicropostState()

  return !publishDate ? (
    <Box textAlign='center' flex={1}>
      New Micropost
    </Box>
  ) : (
    <Box textAlign='center' flex={1}>
      Edit Micropost
    </Box>
  )
}

const AudiencePicker: React.FC = () => {
  const {setCurrentStep} = useModalContext()
  const {isImageUploading} = useAttachmentControlsState()
  const {setValue} = useComposeMicropostActions()
  const {audienceMode, segmentID, dispatch, deleteSegment, status, mode} =
    useSegments()
  const {toast} = useStatusToast()
  const [isSwitchingToEveryone, setIsSwitchingToEveryone] =
    React.useState(false)

  const handleEveryone = async (): Promise<void> => {
    setIsSwitchingToEveryone(true)
    try {
      // If a custom segment was just created in this session (unowned) but
      // never attached to a saved micropost, clean it up before switching
      // to everyone — otherwise the orphan is only collected on modal
      // close.
      if (status === 'unowned' && segmentID) {
        const result = await deleteSegment(segmentID)
        if (result.status === 'failure') {
          // Server cleanup failed; keep audienceMode in sync with what the
          // server still owns so the UI doesn't promise an Everyone send
          // while a custom segment is still attached.
          toast.error('Unable to switch audience. Please try again.')
          return
        }
      }
      dispatch({type: 'SET_AUDIENCE_EVERYONE'})
      setValue('segmentID', null)
    } finally {
      setIsSwitchingToEveryone(false)
    }
  }

  const handleCustom = (): void => {
    setCurrentStep('segment')
  }

  // In edit mode the audience can't be changed, but the user should still be
  // able to click the active pill to open the segment step in read-only
  // mode and review the saved selections (matches production behavior).
  if (mode === 'edit') {
    const isCustom = audienceMode === 'custom'
    return (
      <Flex p={1}>
        <Button
          data-testid={
            isCustom ? 'audience-custom-button' : 'audience-everyone-button'
          }
          aria-label={isCustom ? 'audience-custom' : 'audience-everyone'}
          aria-pressed
          leftIcon={<CustomIcon name='audience' />}
          variant='solid'
          rounded='full'
          size='sm'
          colorScheme='darkgreen'
          fontWeight='normal'
          isDisabled={isImageUploading}
          onClick={handleCustom}>
          {isCustom
            ? copy.buttons.audiencePicker.custom
            : copy.buttons.audiencePicker.everyone}
        </Button>
      </Flex>
    )
  }

  return (
    <Flex direction='column'>
      <Flex p={1} gap={2}>
        <Button
          data-testid='audience-everyone-button'
          aria-label='audience-everyone'
          aria-pressed={audienceMode === 'everyone'}
          leftIcon={<CustomIcon name='audience' />}
          variant={audienceMode === 'everyone' ? 'solid' : 'outline'}
          rounded='full'
          size='sm'
          colorScheme={audienceMode === 'everyone' ? 'darkgreen' : undefined}
          color={
            audienceMode === 'everyone' ? undefined : appTheme.colors.lightText
          }
          fontWeight='normal'
          isDisabled={isImageUploading || isSwitchingToEveryone}
          onClick={handleEveryone}>
          {copy.buttons.audiencePicker.everyone}
        </Button>
        <Button
          data-testid='audience-custom-button'
          aria-label='audience-custom'
          aria-pressed={audienceMode === 'custom'}
          leftIcon={<CustomIcon name='audience' />}
          variant={audienceMode === 'custom' ? 'solid' : 'outline'}
          rounded='full'
          size='sm'
          colorScheme={audienceMode === 'custom' ? 'darkgreen' : undefined}
          color={
            audienceMode === 'custom' ? undefined : appTheme.colors.lightText
          }
          fontWeight='normal'
          isDisabled={isImageUploading}
          onClick={handleCustom}>
          {copy.buttons.audiencePicker.custom}
        </Button>
      </Flex>
      {audienceMode === 'unset' && (
        <Text
          data-testid='audience-picker-hint'
          fontSize='sm'
          color='#69747D'
          px={1}>
          {copy.buttons.audiencePicker.hint}
        </Text>
      )}
    </Flex>
  )
}

const Body: React.FC<React.PropsWithChildren> = () => {
  return (
    <Flex direction='column' h={['100%']}>
      <Flex px={6} py={2}>
        <Feature name={SEGMENT_MICROPOSTS_FEATURE}>
          <AudiencePicker />
        </Feature>
      </Flex>

      <ContentControl px={6} />

      <HyperlinkAttachment.Control />

      <MediaAttachment.Control />
    </Flex>
  )
}

const Footer: React.FC<{onClose(): void}> = ({onClose}) => {
  const {isVideoValid, isHyperlinkValid, ...micropost} =
    useComposeMicropostState()
  const {isImageUploading} = useAttachmentControlsState()
  const {createMicropost, updateMicropost} = useMicropostsStore()
  const [isLoading, setIsLoading] = React.useState(false)
  const {setValue} = useComposeMicropostActions()
  const {audienceMode} = useSegments()
  const {getIsReleasedToUser} = useReleaseFlags()
  const isSegmentFeatureEnabled = getIsReleasedToUser(
    SEGMENT_MICROPOSTS_FEATURE,
  )

  const {toast} = useStatusToast()

  const isValidToSubmit = (): boolean => {
    if (
      micropost.content.length &&
      isVideoValid &&
      isHyperlinkValid &&
      !isImageUploading &&
      (!isSegmentFeatureEnabled || audienceMode !== 'unset')
    ) {
      return true
    }

    return false
  }

  const handleOnSubmit = async (): Promise<void> => {
    setIsLoading(true)

    const response =
      micropost.ID === INSERT_PLACEHOLDER_ID
        ? await createMicropost(micropost)
        : await updateMicropost(micropost)

    setIsLoading(false)

    if (response.status !== 'success') {
      toast.error(response.message)
    } else {
      onClose()
    }
  }

  const toggleWillSendNotification = (): void => {
    setValue('notify', !micropost.notify)
  }

  return (
    <Flex direction={['column', 'row']} flex='1 1 auto'>
      <Flex flex={[1]} justifyContent={['center', 'flex-start']}>
        <HyperlinkAttachment.ControlToggle />
        <MediaAttachment.ImageControlToggle />
        <MediaAttachment.VideoControlToggle />
      </Flex>
      {!micropost.publishDate ? (
        <Flex
          flex={[2]}
          justifyContent={['space-between', 'flex-end']}
          alignItems={['flex-start', 'center']}
          pr={[0, 10]}
          pt={['10px', 0]}
          pb={['20px', '5px']}>
          <Text pr={[0, 5]} color='#69747D'>
            Send push notification
          </Text>
          <Switch
            pt={[0, '5px']}
            data-testid='push-slider'
            size='lg'
            colorScheme='darkgreen'
            variant='info'
            onChange={toggleWillSendNotification}
            isChecked={micropost.notify}
          />
        </Flex>
      ) : (
        <Flex
          flex={[2]}
          justifyContent={['space-between', 'flex-end']}
          alignItems={['center']}
          pr={[0, 10]}
          pb={['20px', '5px']}>
          <Text pr={[0, 5]} color='#69747D'>
            Push notifications can only be sent for new Microposts
          </Text>
          <Switch
            data-testid='push-slider'
            size='lg'
            variant='info'
            ml={['20px', 0]}
            onChange={toggleWillSendNotification}
            isChecked={micropost.notify}
            isDisabled
          />
        </Flex>
      )}

      <ActionButton
        // mr={3}
        onClick={handleOnSubmit}
        isLoading={isLoading}
        loadingText={copy.buttons.confirm.loadingText}
        isDisabled={!isValidToSubmit()}>
        {copy.buttons.confirm.text}
      </ActionButton>
    </Flex>
  )
}

export const ComposeStepUI = {
  Header,
  Body,
  Footer,
}
